// "Start your shift" (ADR-0031; spec/13 §10): right after console sign-in, anyone who can take payments at the venue and
// holds no drawer is asked to start their shift — when the venue counts its drawers per shift (`shifts.enabled`).
//
//   per_drawer             pick the counter (each shows Free, or "Riya since 18:00"; this device's counter pre-selected,
//                          remembered per browser), count the float by notes and coins, Start shift.
//   shared_drawer          the shared drawer open → Join the shift (no float, shows who is on it); not open → start it.
//   floor_without_drawer   the same as per drawer, with "Not handling cash today" first.
//
// "Not handling cash today" and the close button dismiss it — anyone may. Only cash needs a shift (card, UPI and wallet
// work without one), so dismissing leaves the Cash tender locked with "Start a shift to take cash", which reopens this.
//
// The dialog is the venue shell's (`ShiftPromptProvider`), so Sell, Orders and Wallet open the same one. It opens by
// itself once per sign-in: the sign-in page leaves a flag in sessionStorage, and the first venue screen after it reads
// and clears it. The server owns every rule — this only asks.

import type { BusinessDayStatus, Money, RunningDrawer } from '@clavian/api-client';
import { useTranslation, useVenueFormatter } from '@clavian/i18n/react';
import { Button, cx, Dialog, TextField, useToast } from '@clavian/ui-web';
import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';

import { useAccess } from '../../api/queries';
import { useRefusalText } from './refusal';
import type { MyVenue } from '../../api/me';
import { Icon } from '../../shell/nav-icons';
import { Notice } from '../../ui';
import { localDateLabel } from './days-and-shifts';
import { useBusinessDayStatus, useCounters, useCreateCounter, useShiftMembership } from './queries';
import { CountDrawerFlow, StartShiftFlow } from './ShiftFlows';
import { SignOutGuardContext, type SignOutGuard } from '../../shell/AppShell';

import './shift-prompt.css';

const SHIFTS_OWN = 'shifts:own';
const SETTINGS_PAYMENTS = 'settings:payments';
/** Taking a payment at the counter: a sale's tender, or a wallet top-up. */
const TAKES_PAYMENTS = ['orders:create', 'wallet:topup'] as const;

/** sessionStorage: set by the sign-in page, read (and cleared) by the first venue screen after it. */
export const SHIFT_PROMPT_PENDING = 'clavian.shiftPrompt.pending';
/** localStorage: the counter this browser's drawer is at, per venue. */
const COUNTER_KEY = (venueId: string) => `clavian.counter.${venueId}`;

/** Ask for the prompt on the next venue screen (the sign-in page calls it). Never throws. */
export function requestShiftPrompt(): void {
  try {
    window.sessionStorage.setItem(SHIFT_PROMPT_PENDING, '1');
  } catch {
    // Storage blocked: the prompt simply does not open by itself; Start shift stays one tap away.
  }
}

function takePendingPrompt(): boolean {
  try {
    const pending = window.sessionStorage.getItem(SHIFT_PROMPT_PENDING) === '1';
    if (pending) window.sessionStorage.removeItem(SHIFT_PROMPT_PENDING);
    return pending;
  } catch {
    return false;
  }
}

export function rememberedCounter(venueId: string): string | null {
  try {
    const value = window.localStorage.getItem(COUNTER_KEY(venueId));
    return value === null || value.trim() === '' ? null : value;
  } catch {
    return null;
  }
}

function rememberCounter(venueId: string, name: string | null): void {
  try {
    if (name === null) window.localStorage.removeItem(COUNTER_KEY(venueId));
    else window.localStorage.setItem(COUNTER_KEY(venueId), name);
  } catch {
    // Not remembered on this browser; the next prompt asks again.
  }
}

/** Does the caller hold an open drawer here (one they opened or joined)? */
export function holdsDrawer(status: Pick<BusinessDayStatus, 'drawers'>, memberId: string | null | undefined): boolean {
  if (memberId === null) return false;
  if (memberId === undefined) return false;
  return status.drawers.some((drawer) => drawer.openedByMemberId === memberId || drawer.members.some((member) => member.memberId === memberId));
}

/**
 * Is the Cash tender locked for the caller (ADR-0031)? Only when the venue counts drawers per shift and the caller is on
 * no open drawer — except at a shared-drawer venue whose one drawer is open (cash goes into it). Unknown while the day's
 * status loads, and then not locked: the server has the last word either way (`cash-needs-shift`).
 */
export function useCashLocked(venue: Pick<MyVenue, 'id' | 'organizationId'>): boolean {
  const access = useAccess({ venueId: venue.id });
  const status = useBusinessDayStatus(venue.organizationId, venue.id).data;
  if (status === undefined || access.status !== 'ready' || !status.shiftsEnabled) return false;
  if (holdsDrawer(status, access.memberId)) return false;
  if (status.cashHandling === 'shared_drawer' && status.drawers.length === 1) return false;
  return true;
}

interface ShiftPromptValue {
  /** Open "Start your shift". */
  readonly open: () => void;
}

const ShiftPromptContext = createContext<ShiftPromptValue | null>(null);

/** The venue's "Start your shift" dialog, for any screen to open; undefined outside a venue. */
export function useShiftPrompt(): ShiftPromptValue | null {
  return useContext(ShiftPromptContext);
}

/** The venue shell's provider: the dialog, opened by itself once after sign-in, or by `useShiftPrompt().open()`. */
export function ShiftPromptProvider({ venue, children }: { readonly venue: MyVenue; readonly children: ReactNode }): ReactNode {
  const access = useAccess({ venueId: venue.id });
  const status = useBusinessDayStatus(venue.organizationId, venue.id);
  const [open, setOpen] = useState(false);
  // Once per sign-in: the flag is read (and cleared) when the shell first renders; dismissing ends the automatic open.
  const [pending] = useState(() => takePendingPrompt());
  const [autoDismissed, setAutoDismissed] = useState(false);
  const value = useMemo<ShiftPromptValue>(() => ({ open: () => setOpen(true) }), []);

  const eligible =
    access.status === 'ready' && access.can(SHIFTS_OWN) && TAKES_PAYMENTS.some((permission) => access.can(permission)) && status.data !== undefined && status.data.shiftsEnabled;
  const autoOpen = pending && !autoDismissed && eligible && status.data !== undefined && access.status === 'ready' && !holdsDrawer(status.data, access.memberId);
  const onOpenChange = (next: boolean) => {
    setOpen(next);
    if (!next) setAutoDismissed(true);
  };

  // ADR-0032: signing out while holding a drawer asks first — hand over, end the shift, or sign out anyway.
  const held =
    status.data !== undefined && access.status === 'ready' && access.memberId !== null
      ? (status.data.drawers.find((drawer) => drawer.members.some((member) => member.memberId === access.memberId) || (drawer.members.length === 0 && drawer.openedByMemberId === access.memberId)) ?? null)
      : null;
  const [leaving, setLeaving] = useState<(() => Promise<void>) | null>(null);
  const guard = useCallback<SignOutGuard>(
    (signOutNow) => {
      if (held === null) return false;
      setLeaving(() => signOutNow);
      return true;
    },
    [held],
  );

  return (
    <ShiftPromptContext.Provider value={value}>
      <SignOutGuardContext.Provider value={guard}>
        {children}
        {leaving !== null && held !== null ? (
          <HoldingDrawerDialog venue={venue} drawer={held} signOutNow={leaving} onClose={() => setLeaving(null)} />
        ) : null}
      </SignOutGuardContext.Provider>
      {status.data !== undefined && access.status === 'ready' ? (
        <StartYourShiftDialog open={open || autoOpen} onOpenChange={onOpenChange} venue={venue} status={status.data} memberId={access.memberId} canStart={access.can(SHIFTS_OWN)} canManage={access.can(SETTINGS_PAYMENTS)} />
      ) : null}
    </ShiftPromptContext.Provider>
  );
}

/** One counter in the picker: free, or held by someone since a time. */
interface CounterChoice {
  readonly key: string;
  /** The counter's name; null for the venue's one unnamed counter. */
  readonly name: string | null;
  readonly heldBy: string | null;
  readonly since: string | null;
  /** The counter of the venue's list (ADR-0032), when it is one. */
  readonly counterId?: string;
  /** The float pre-filled: the counter's last closing count, or its default float. */
  readonly float?: Money;
}

const NEW_COUNTER = '__new__';

interface PromptProps {
  readonly onOpenChange: (open: boolean) => void;
  readonly venue: MyVenue;
  readonly status: BusinessDayStatus;
  readonly memberId: string | null;
  readonly canStart: boolean;
  /** May add a counter (`settings:payments`): the picker offers "+ New counter". */
  readonly canManage: boolean;
}

function StartYourShiftDialog({ open, ...props }: PromptProps & { readonly open: boolean }): ReactNode {
  const { t } = useTranslation('console');
  return (
    <Dialog open={open} onOpenChange={props.onOpenChange} size="md" dirty="auto" title={t('shifts.prompt.title')} description={t(`shifts.prompt.intro.${props.status.cashHandling}`)}>
      {/* Mounted while open, so every opening starts from the first step with this device's counter picked. */}
      {open ? <PromptBody {...props} /> : null}
    </Dialog>
  );
}

function PromptBody({ onOpenChange, venue, status, memberId, canStart, canManage }: PromptProps): ReactNode {
  const { t } = useTranslation('console');
  const refusalText = useRefusalText();
  const format = useVenueFormatter();
  const { show } = useToast();
  const join = useShiftMembership(venue.organizationId, venue.id);
  const create = useCreateCounter(venue.organizationId, venue.id);
  const board = useCounters(venue.organizationId, venue.id).data;
  const mode = status.cashHandling;
  const holds = holdsDrawer(status, memberId);
  const shared = mode === 'shared_drawer' ? (status.drawers[0] ?? null) : null;
  const remembered = rememberedCounter(venue.id);
  const managed = board !== undefined && board.counters.length > 0;
  const [step, setStep] = useState<'counter' | 'float'>('counter');
  const [newName, setNewName] = useState('');
  const [created, setCreated] = useState<CounterChoice | null>(null);
  const [error, setError] = useState<string | undefined>(undefined);

  const namesOf = (drawer: BusinessDayStatus['drawers'][number]) =>
    (drawer.members.length > 0 ? drawer.members.map((member) => member.name ?? t('shifts.holderUnknown')) : [drawer.openedByName ?? t('shifts.holderUnknown')]).join(', ');
  const runningNames = (running: RunningDrawer) =>
    running.members
      .filter((member) => member.leftAt === null)
      .map((member) => member.name ?? t('shifts.holderUnknown'))
      .join(', ');

  // ADR-0032: the venue's counters (each free, or held since a time); before any is set up, the drawers open now and
  // this device's remembered counter, as before.
  const choices = useMemo<CounterChoice[]>(() => {
    if (board !== undefined && board.counters.length > 0) {
      return [
        ...board.counters.map((counter) => ({
          key: counter.id,
          counterId: counter.id,
          name: counter.name,
          heldBy: counter.running === null ? null : runningNames(counter.running),
          since: counter.running === null ? null : format.time(counter.running.openedAt),
          float: counter.lastClosingCount ?? counter.defaultFloat,
        })),
        ...board.unassigned.map((running) => ({
          key: `held:${running.shiftId}`,
          name: running.counterName,
          heldBy: runningNames(running),
          since: format.time(running.openedAt),
        })),
      ];
    }
    const list: CounterChoice[] = status.drawers.map((drawer) => ({
      key: `held:${drawer.shiftId}`,
      name: drawer.counterName,
      heldBy: namesOf(drawer),
      since: format.time(drawer.openedAt),
    }));
    const taken = (name: string | null) => list.some((item) => (item.name ?? '').toLowerCase() === (name ?? '').toLowerCase());
    if (remembered !== null && !taken(remembered)) list.unshift({ key: `free:${remembered}`, name: remembered, heldBy: null, since: null });
    if (list.length === 0) list.push({ key: 'free:', name: null, heldBy: null, since: null });
    return list;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- recomputed when the counters, the drawers or the remembered counter change
  }, [board, status.drawers, remembered, format]);

  const firstChoice = () => {
    const free = choices.filter((item) => item.heldBy === null);
    return (free.find((item) => remembered !== null && (item.name ?? '').toLowerCase() === remembered.toLowerCase()) ?? free[0])?.key ?? NEW_COUNTER;
  };
  const [choice, setChoice] = useState<string | null>(null);
  const current = choice ?? firstChoice();
  const offerNew = !managed || canManage;

  const dismiss = () => onOpenChange(false);
  const picked = created ?? choices.find((item) => item.key === current);
  const counterName = current === NEW_COUNTER && created === null ? newName.trim() : (picked?.name ?? null);
  const dayLabel = localDateLabel(status.businessDay, format.locale);

  function next(): void {
    if (current === NEW_COUNTER && created === null) {
      if (newName.trim() === '') {
        setError(t('shifts.prompt.counterNameRequired'));
        return;
      }
      if (managed) {
        // A manager's "+ New counter" joins the venue's list (Settings → Counters), then the float is counted there.
        create.mutate(
          { name: newName.trim() },
          {
            onSuccess: (counter) => {
              setCreated({ key: counter.id, counterId: counter.id, name: counter.name, heldBy: null, since: null, float: counter.defaultFloat });
              setError(undefined);
              setStep('float');
            },
            onError: (err) => setError(refusalText(err, t('shifts.error.open'))),
          },
        );
        return;
      }
    }
    setError(undefined);
    setStep('float');
  }

  function joinShared(shiftId: string): void {
    join.mutate(
      { shiftId, action: 'join' },
      {
        onSuccess: () => {
          show({ tone: 'ok', title: t('shifts.prompt.joined') });
          onOpenChange(false);
        },
        onError: (err) => setError(refusalText(err, t('shifts.error.open'))),
      },
    );
  }

  const notHandling = (
    <Button variant={mode === 'floor_without_drawer' ? 'secondary' : 'ghost'} block={mode === 'floor_without_drawer'} onClick={dismiss}>
      {t('shifts.prompt.notHandling')}
    </Button>
  );

  let body: ReactNode;
  if (holds) {
    body = <Notice tone="ok">{t('shifts.prompt.alreadyHolding')}</Notice>;
  } else if (!canStart) {
    body = <Notice tone="info">{t('shifts.prompt.cannotStart')}</Notice>;
  } else if (shared !== null) {
    body = (
      <div className="console-form">
        <div className="console-shift-prompt__shared">
          <span className="console-eyebrow">{t('shifts.prompt.sharedOpen')}</span>
          <strong>{namesOf(shared)}</strong>
          <span className="console-subtle">{t('shifts.prompt.sharedSince', { time: format.time(shared.openedAt), float: format.money(shared.openingFloat, 'noFraction') })}</span>
        </div>
        {error !== undefined ? (
          <Notice tone="danger" role="alert">
            {error}
          </Notice>
        ) : null}
        <p className="console-muted">{t('shifts.prompt.joinHint')}</p>
        <div className="console-form__actions console-form__actions--end">
          {notHandling}
          <Button variant="primary" busy={join.isPending} onClick={() => joinShared(shared.shiftId)}>
            {t('shifts.prompt.join')}
          </Button>
        </div>
      </div>
    );
  } else if (step === 'float' || mode === 'shared_drawer') {
    const sharedStart = mode === 'shared_drawer';
    body = (
      <>
        {!sharedStart ? <p className="console-muted">{t('shifts.prompt.atCounter', { counter: counterName ?? t('shifts.noCounter') })}</p> : null}
        <StartShiftFlow
          currency={venue.currency ?? 'INR'}
          organizationId={venue.organizationId}
          venueId={venue.id}
          dayLabel={dayLabel}
          fixedCounterName={sharedStart ? null : counterName}
          counterId={sharedStart ? undefined : picked?.counterId}
          initialFloat={sharedStart ? undefined : picked?.float}
          onStarted={() => rememberCounter(venue.id, sharedStart ? null : counterName)}
          onClose={sharedStart ? dismiss : () => setStep('counter')}
          closeLabel={sharedStart ? t('shifts.prompt.notHandling') : undefined}
          onDone={dismiss}
        />
      </>
    );
  } else {
    body = (
      <div className="console-form">
        {mode === 'floor_without_drawer' ? (
          <div className="console-shift-prompt__floor">
            <p className="console-muted">{t('shifts.prompt.floorHint')}</p>
            {notHandling}
          </div>
        ) : null}
        {error !== undefined ? (
          <Notice tone="danger" role="alert">
            {error}
          </Notice>
        ) : null}
        <fieldset className="console-shift-prompt__counters">
          <legend className="clav-field__label">{t('shifts.prompt.counter')}</legend>
          {choices.map((item) => (
            <label key={item.key} className={cx('console-shift-prompt__counter', current === item.key && 'is-checked', item.heldBy !== null && 'is-held')}>
              <input type="radio" className="clav-visually-hidden" name="shift-counter" value={item.key} checked={current === item.key} disabled={item.heldBy !== null} onChange={() => setChoice(item.key)} />
              <span className="console-shift-prompt__counter-name">{item.name ?? t('shifts.noCounter')}</span>
              <span className={cx('console-shift-prompt__counter-status', item.heldBy === null && 'is-free')}>
                {item.heldBy === null ? t('shifts.prompt.free') : t('shifts.prompt.heldSince', { names: item.heldBy, time: item.since ?? '' })}
              </span>
              {remembered !== null && (item.name ?? '').toLowerCase() === remembered.toLowerCase() ? <span className="console-shift-prompt__device">{t('shifts.prompt.thisDevice')}</span> : null}
            </label>
          ))}
          {offerNew ? (
            <label className={cx('console-shift-prompt__counter', current === NEW_COUNTER && 'is-checked')}>
              <input type="radio" className="clav-visually-hidden" name="shift-counter" value={NEW_COUNTER} checked={current === NEW_COUNTER} onChange={() => setChoice(NEW_COUNTER)} />
              <span className="console-shift-prompt__counter-name">
                <Icon name="plus" /> {managed ? t('shifts.prompt.newCounter') : t('shifts.prompt.otherCounter')}
              </span>
            </label>
          ) : null}
        </fieldset>
        {current === NEW_COUNTER ? <TextField label={t('shifts.field.counterName')} required value={newName} onChange={(event) => setNewName(event.target.value)} /> : null}
        <div className="console-form__actions console-form__actions--end">
          {mode === 'floor_without_drawer' ? null : notHandling}
          <Button variant="primary" busy={create.isPending} onClick={next}>
            {t('shifts.prompt.countFloat')}
          </Button>
        </div>
      </div>
    );
  }

  return <div className="console-shift-prompt">{body}</div>;
}

/**
 * "You're still holding the Front desk drawer" (ADR-0032), asked on sign-out: **Hand over** leaves the drawer for the
 * next person, who counts it as they take it over (ADR-0029); **End shift** counts it now, then signs out; **Sign out
 * anyway** leaves the shift open, as before.
 */
function HoldingDrawerDialog({
  venue,
  drawer,
  signOutNow,
  onClose,
}: {
  readonly venue: MyVenue;
  readonly drawer: BusinessDayStatus['drawers'][number];
  readonly signOutNow: () => Promise<void>;
  readonly onClose: () => void;
}): ReactNode {
  const { t } = useTranslation('console');
  const [ending, setEnding] = useState(false);
  const [handingOver, setHandingOver] = useState(false);
  const counter = drawer.counterName ?? t('shifts.noCounter');
  if (ending) {
    return (
      <Dialog open onOpenChange={(next) => (next ? undefined : onClose())} size="md" dirty="auto" title={t('shifts.flow.endTitle')}>
        <CountDrawerFlow
          mode="end"
          currency={venue.currency ?? 'INR'}
          shiftId={drawer.shiftId}
          ownShift
          holderName={drawer.openedByName ?? ''}
          organizationId={venue.organizationId}
          venueId={venue.id}
          onClose={() => void signOutNow()}
        />
      </Dialog>
    );
  }
  return (
    <Dialog
      open
      onOpenChange={(next) => (next ? undefined : onClose())}
      title={t('shell.holding.title', { counter })}
      description={handingOver ? t('shell.holding.handOverBody') : t('shell.holding.body')}
      footer={
        handingOver ? (
          <>
            <Button variant="ghost" onClick={() => setHandingOver(false)}>
              {t('common.back')}
            </Button>
            <Button variant="primary" onClick={() => void signOutNow()}>
              {t('shell.holding.handOverConfirm')}
            </Button>
          </>
        ) : (
          <>
            <Button variant="ghost" onClick={() => void signOutNow()}>
              {t('shell.holding.signOutAnyway')}
            </Button>
            <Button variant="secondary" onClick={() => setHandingOver(true)}>
              {t('shell.holding.handOver')}
            </Button>
            <Button variant="primary" onClick={() => setEnding(true)}>
              {t('shell.holding.end')}
            </Button>
          </>
        )
      }
    >
      <span />
    </Dialog>
  );
}

/** "Start a shift to take cash" — the locked Cash tender's line, with the button that opens the prompt. */
export function CashLockedNotice(): ReactNode {
  const { t } = useTranslation('console');
  const prompt = useShiftPrompt();
  return (
    <div className="console-cash-lock" role="note">
      <span className="console-cash-lock__icon" aria-hidden="true">
        <Icon name="cash" />
      </span>
      <span className="console-cash-lock__text">{t('shifts.prompt.cashLocked')}</span>
      {prompt !== null ? (
        <Button variant="secondary" size="sm" onClick={prompt.open}>
          {t('shifts.startShort')}
        </Button>
      ) : null}
    </div>
  );
}
